/**
 * Admin -> Consent Source (alt-CMP)
 *
 * Chooses WHICH consent ledger the consent-aware query firewall checks before
 * releasing personal data. Resolution order is: the vendor configured here,
 * then FOCTTA privacy core if paired, then fail-closed.
 *
 * This surface exists so a standalone (no privacy-core) customer can run the
 * consent firewall against their own CMP without us hand-seeding a database
 * row. The vendor credential is encrypted at rest and never returned.
 *
 * Backend: GET / PUT / POST-test / DELETE at /v1/admin/consent-source.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  Zap,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  getConsentSourceConfig,
  upsertConsentSourceConfig,
  testConsentSource,
  clearConsentSourceConfig,
  type ConsentSourceAdminState,
  type ConsentSourceTestResult,
} from '@/lib/api';

const INPUT =
  'glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40';

export function AdminConsentSource(): JSX.Element {
  const [state, setState] = useState<ConsentSourceAdminState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [kind, setKind] = useState<string>('none');
  const [enabled, setEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [hasStoredSecret, setHasStoredSecret] = useState(false);

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ConsentSourceTestResult | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getConsentSourceConfig();
      setState(s);
      if (s.config) {
        setKind(s.config.kind);
        setEnabled(s.config.enabled);
        setHasStoredSecret(s.config.hasSecret);
        const cfg = s.config.config ?? {};
        setBaseUrl(typeof cfg['baseUrl'] === 'string' ? (cfg['baseUrl'] as string) : '');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load consent source');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const entry = state?.catalogue.find((c) => c.kind === kind);
  const needsVendorFields = kind !== 'none';

  async function onSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const config: Record<string, unknown> = {};
      if (needsVendorFields) {
        config['baseUrl'] = baseUrl.trim();
        if (secret.trim() && entry?.secretField) config[entry.secretField] = secret.trim();
      }
      await upsertConsentSourceConfig({ kind, config, enabled });
      setSecret('');
      setSaved(true);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onTest() {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await testConsentSource());
    } catch (e) {
      setTestResult({
        ok: false,
        elapsedMs: 0,
        reachable: false,
        error: e instanceof Error ? e.message : 'Test failed',
      });
    } finally {
      setTesting(false);
    }
  }

  async function onClear() {
    setSaving(true);
    try {
      await clearConsentSourceConfig();
      setKind('none');
      setEnabled(false);
      setBaseUrl('');
      setSecret('');
      setHasStoredSecret(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Consent source"
        subtitle="Choose which consent ledger the query firewall checks before releasing personal data."
        badges={[
          { label: state?.config?.enabled ? `Active: ${state.config.kind}` : 'Not configured', variant: state?.config?.enabled ? 'success' : 'glass' },
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        }
      />

      {loading && <LoadingState caption="Loading consent source..." />}
      {error && !loading && (
        <ErrorState title="Consent source" detail={error} onRetry={() => void load()} />
      )}

      {!loading && state && (
        <>
          <GlassCard className="p-4 mb-4">
            <CardHeader className="p-0 mb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> How this is resolved
              </CardTitle>
              <CardDescription className="text-xs">
                The firewall checks the vendor configured here first. If none is set, it falls back
                to FOCTTA privacy core when paired, and otherwise fails closed - it never silently
                allows access to data without a consent answer.
              </CardDescription>
            </CardHeader>
          </GlassCard>

          <GlassCard className="p-5 space-y-4">
            <div>
              <label htmlFor="cs-kind" className="text-xs font-medium block mb-1">
                Consent vendor
              </label>
              <select
                id="cs-kind"
                className={INPUT}
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value);
                  setTestResult(null);
                }}
              >
                {state.catalogue.map((c) => (
                  <option key={c.kind} value={c.kind}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            {needsVendorFields && (
              <>
                <div>
                  <label htmlFor="cs-url" className="text-xs font-medium block mb-1">
                    API base URL
                  </label>
                  <input
                    id="cs-url"
                    className={INPUT}
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://api.your-cmp.com"
                  />
                </div>
                <div>
                  <label htmlFor="cs-secret" className="text-xs font-medium block mb-1">
                    {entry?.secretField === 'apiKey' ? 'API key' : 'API token'}
                    {hasStoredSecret && (
                      <Badge variant="success" className="ml-2 text-[9px]">
                        Stored
                      </Badge>
                    )}
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="cs-secret"
                      className={INPUT}
                      type={showSecret ? 'text' : 'password'}
                      value={secret}
                      onChange={(e) => setSecret(e.target.value)}
                      placeholder={hasStoredSecret ? 'Leave blank to keep the stored credential' : 'Paste the credential'}
                    />
                    <Button size="sm" variant="glass" type="button" onClick={() => setShowSecret((v) => !v)}>
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Encrypted at rest and never displayed again after saving.
                  </div>
                </div>
              </>
            )}

            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enabled (the firewall will use this source)
            </label>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button size="sm" onClick={() => void onSave()} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
              </Button>
              <Button size="sm" variant="glass" onClick={() => void onTest()} disabled={testing}>
                {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />} Test connection
              </Button>
              {state.config && (
                <Button size="sm" variant="glass" onClick={() => void onClear()} disabled={saving}>
                  <Trash2 className="h-3 w-3" /> Clear
                </Button>
              )}
              {saved && (
                <span className="text-[11px] text-success flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Saved
                </span>
              )}
            </div>

            {testResult && (
              <div
                className={`glass-lite rounded-md p-3 text-xs border ${
                  testResult.ok ? 'border-success/40' : 'border-destructive/40'
                }`}
              >
                <div className="flex items-center gap-2 font-medium">
                  {testResult.ok ? (
                    <CheckCircle2 className="h-4 w-4 text-success" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-destructive" />
                  )}
                  {testResult.ok ? 'Vendor reachable' : 'Test failed'}
                  <span className="text-muted-foreground font-normal">({testResult.elapsedMs} ms)</span>
                </div>
                {testResult.note && (
                  <div className="text-muted-foreground mt-1">{testResult.note}</div>
                )}
                {testResult.error && <div className="text-destructive mt-1">{testResult.error}</div>}
              </div>
            )}
          </GlassCard>
        </>
      )}
    </PageContainer>
  );
}
